import {fromJS, Map, List} from 'immutable';
import {CLEAN, REQUEST, STATE} from '../constants/index';
import {isArrayNotEmpty} from '../utils/js/array';
import {types} from '../utils/js/types';
import initialState from '../initialState';

/**
 * General action handling for clean state updates
 * @param {Object} state An immutable state object to check against
 * @param {Object} action Data to update the redux store state
 * @returns {Object} A (usually changed) immutable state object
 */
export default function (state = Map(), action = null) {
  switch (action.type) {
    // delete data from gmi.data[action.dataKey][action.storeKey]
    case CLEAN.CLEAN_DATA_BY_STORE_KEY: {
      const {dataKey, storeKey} = action;
      if (types(dataKey).isString && types(storeKey).isString) {
        return state.deleteIn(['gmi', 'data'].concat([dataKey, storeKey]));
      }
      return state;
    }

    // if passed an array of locationIds, delete each one at a time from state
    // else, just reset the entire locations.by_id state to {}
    case CLEAN.CLEAN_LOCATIONS_STATE: {
      if (isArrayNotEmpty(action.locationIds)) {
        action.locationIds.forEach(id => state = state.deleteIn(STATE.GMA.LOCATION.BY_ID.concat(id)));
        return state;
      } else {
        return state.setIn(STATE.GMA.LOCATION.BY_ID, Map());
      }
    }

    // clean out error messaging by service type
    case CLEAN.CLEAN_MESSAGES: {
      const serviceTypes = isArrayNotEmpty(action.serviceTypes) ? action.serviceTypes : [REQUEST.GMA, REQUEST.SOLR];
      serviceTypes.forEach(serviceType => {
        const type = serviceType.toUpperCase();
        state = state.setIn(STATE.MESSAGES[type], List());
        state = state.setIn(STATE.MESSAGES[`${type}_SERVICES`], Map());
      });
      return state;
    }

    // reset gma.reservation to its initialState
    case CLEAN.CLEAN_PROFILE_STATE: {
      // everything in gmi.gma.trips is profile'y except retrieve, so keep that
      const next = fromJS(initialState.gmi.gma.trips).set('retrieve', state.getIn(['gmi', 'gma', 'trips', 'retrieve']));
      state = state.setIn(['gmi', 'gma', 'trips'], next);
      state = state.setIn(STATE.GMA.REWARDS.REWARDS_PATH, fromJS(initialState.gmi.gma.rewards));
      state = state.setIn(STATE.UI.REWARDS.HISTORY_PATH, fromJS(initialState.gmi.ui.rewards.history));
      return state;
    }

    // reset gma.reservation to its initialState
    case CLEAN.CLEAN_RESERVATION_STATE: {
      if (action.cleanState) {
        if (action.cleanState.cleanCarClassState) state = state.setIn(STATE.UI.RESERVATIONS.CAR_CLASS, fromJS(initialState.gmi.ui.reservations.car_class));
        if (action.cleanState.cleanCommitState) state = state.setIn(STATE.UI.RESERVATIONS.COMMIT, Map());
        if (action.cleanState.cleanExtrasState) state = state.setIn(STATE.UI.RESERVATIONS.EXTRAS, List());
        if (action.cleanState.cleanGmaReservationState) state = state.setIn(['gmi', 'gma', 'reservation'], fromJS(initialState.gmi.gma.reservation));
        if (action.cleanState.cleanInitiateState) state = state.setIn(STATE.UI.RESERVATIONS.INITIATE, Map());
        if (action.cleanState.cleanVehicleFilters) state = state.deleteIn(STATE.UI.FILTERS.concat(['car_classes']));
        if (action.cleanState.cleanDriverProfile) state = state.setIn(STATE.UI.RESERVATIONS.DRIVER_PROFILE, fromJS(initialState.gmi.ui.reservations.driver_profile));
      }
      return state;
    }

    // if passed an array of endpoints, handle each individually
    // else, just reset the entire gma.reservation to its initialState
    case CLEAN.CLEAN_SOLR_STATE: {
      if (isArrayNotEmpty(action.endpoints)) {
        action.endpoints.forEach(endpoint => {
          const {key, data} = endpoint;
          if (isArrayNotEmpty(data)) {
            state = state.deleteIn(STATE.SOLR[key].concat(data));
            if (data.length > 1) {
              const firstPath = STATE.SOLR[key].concat([data[0]]);
              if (state.getIn(firstPath).isEmpty()) {
                state = state.deleteIn(firstPath);
              }
            }
          } else {
            state = state.setIn(STATE.SOLR[key], Map());
          }
        });
        return state;
      } else {
        return state.setIn(['gmi', 'solr'], fromJS(initialState.gmi.solr));
      }
    }

    default:
      return state;
  }
}
